/**
 * Service worker registration for the PWA.
 *
 * `vite-plugin-pwa` exposes a virtual module that returns the
 * `registerSW` function. Calling it with `immediate: true` activates the
 * worker as soon as the page boots; the `onNeedRefresh` callback fires
 * when a new build is detected.
 *
 * The plugin runs in `registerType: 'prompt'` mode (set in vite.config.ts)
 * so updates don't auto-skip-waiting. Instead, `onNeedRefresh` surfaces a
 * persistent toast (PWAUpdatePromptBody) with a "Reload" button — clicking
 * it calls `updateSW(true)`, which makes the waiting worker take control
 * and reloads the page. Dismissing the toast keeps the user on the old
 * version until they next reload naturally.
 *
 * Imported only in `main.tsx` and only behaves at runtime in production
 * builds — `devOptions.enabled: false` in `vite.config.ts` means dev runs
 * skip the worker entirely.
 *
 * Shared shape across PE, admin, and webapp so the three apps register
 * their PWAs identically; only `APP_NAME` and the logger differ.
 */

// `virtual:pwa-register` is a virtual module exposed by vite-plugin-pwa.
// eslint-plugin-import isn't wired into the flat config in any of the 3
// apps, so no `import/no-unresolved` disable is needed here.
import { registerSW } from 'virtual:pwa-register';
import { toast } from 'react-toastify';
import logger from './logger';
import { PWAUpdatePromptBody } from '@/components/common/PWAUpdatePromptBody';

const APP_NAME = 'DomainBoost';
const UPDATE_TOAST_ID = 'pwa-update-available';

export const registerServiceWorker = (): void => {
  // Skip in dev — vite-plugin-pwa is configured with devOptions.enabled=false.
  if (import.meta.env.DEV) return;
  if (typeof window === 'undefined' || !('serviceWorker' in navigator)) return;

  try {
    // Capture updateSW so onNeedRefresh can call it from its closure.
    // The assignment happens synchronously here; onNeedRefresh fires later
    // (after a fresh build is detected), by which point updateSW is set.
    let updateSW: ((reloadPage?: boolean) => Promise<void>) | null = null;
    updateSW = registerSW({
      immediate: true,
      onRegisteredSW(swUrl) {
        logger.info(`[PWA] Service worker registered: ${swUrl}`);
      },
      onOfflineReady() {
        logger.info('[PWA] App is ready to work offline');
      },
      onNeedRefresh() {
        logger.info('[PWA] New version available — surfacing reload prompt');
        toast.info(
          <PWAUpdatePromptBody
            appName={APP_NAME}
            onReload={() => {
              void updateSW?.(true);
            }}
          />,
          {
            autoClose: false,
            closeOnClick: false,
            draggable: false,
            icon: false,
            toastId: UPDATE_TOAST_ID,
            className: 'pwa-update-toast',
          }
        );
      },
      onRegisterError(error) {
        // Don't surface to the user — service-worker failure is a soft
        // degradation (the app still works, just without offline + auto-update).
        const errObj = error instanceof Error ? error : new Error('SW registration failed');
        logger.error('[PWA] Service worker registration failed', errObj);
      },
    });
  } catch (error) {
    const errObj = error instanceof Error ? error : new Error('Failed to register service worker');
    logger.error('[PWA] Service worker bootstrap threw', errObj);
  }
};
